import { Button, DisplayText } from '@superops/ui-design-system';

import {
  BackgroundIcon,
  ERROR_CODES,
  getTranslatedText
} from '@nile/components';
import ActiveIntegrationIcon from '@nile/client_management/src/Components/ActiveInvoiceBillingIntegrationIcon';
import React, { useState } from 'react';
import dompurify from 'dompurify';
import envConfig from '@nile/home/src/Config/env';
import { FormattedMessage, injectIntl } from 'react-intl';
import { ERROR_CODES_ITEM_MAP } from '@nile/client_management/src/Constants/invoice';
import { Tooltip } from 'react-clone-tippy';
import styled from 'styled-components';
import { SVGIcon } from '@superops/super-icons';
import { dompurifier } from '@nile/components/src/Utils/helpers';

/**
 * Cells are specific to the components using the table.
 * This action cell is used to push the invoice to third party.
 */

const integrationMap = {
  QUICK_BOOKS_DESKTOP: {
    push: 'invoice.actions.pushToQuickbooksDesktop',
    repush: 'invoice.actions.pushToQuickbooksDesktop'
  },
  QUICK_BOOKS_ONLINE: {
    push: 'invoice.actions.pushToQuickbooks',
    repush: 'invoice.actions.repushToQuickbooks'
  },
  XERO: {
    push: 'invoice.actions.pushToXero',
    repush: 'invoice.actions.repushToXero'
  },
  DEFAULT: {}
};

const concatErrorsArray = (errorsArray, intl) => {
  return errorsArray.reduce((acc, error) => {
    let items = '';

    if (error.params) {
      const itemsArray =
        error.params[ERROR_CODES_ITEM_MAP[error.errorCode]] ||
        error.params.items ||
        [];
      const itemStrings = itemsArray.map((item) => `${item.name}`);
      items = `(${itemStrings.join(', ')})`;
    }

    return `${acc}${getTranslatedText(
      intl,
      ERROR_CODES[error.errorCode] || error.message || ' '
    )} ${items}\n`;
  }, '');
};

const getIntegrationErrorMessage = (col, intl) => {
  const { pushPaymentToIntegratedAppError, pushedToIntegratedAppError } = col;
  let errors = [];
  if (pushedToIntegratedAppError) errors = [...pushedToIntegratedAppError];
  if (pushPaymentToIntegratedAppError)
    errors = [...errors, ...pushPaymentToIntegratedAppError];

  return concatErrorsArray(errors, intl);
};

function InvoiceIntegrationStatusCell(props) {
  const { cell, intl } = props;
  const meta = cell?.row?.original;
  const [actionLoading, setActionLoading] = useState(false);
  const { actions, integrationType = 'DEFAULT' } = cell.column;

  const status = meta?.pushedToIntegratedAppStatus;

  const pushedToAppLoading = status === 'PROGRESS';

  const { loadingState } = cell;
  const pushActionKey =
    meta.pushedToIntegratedAppStatus !== null ||
    (meta.statusEnum === 'PAID' &&
      (!meta.pushPaymentToIntegratedAppStatus ||
        meta.pushPaymentToIntegratedAppStatus === 'FAILURE'))
      ? 'repush'
      : 'push';

  const renderStatus = () => {
    const errorMessage =
      status === 'FAILURE' ? getIntegrationErrorMessage(meta, intl) : null;
    return (
      <div className="flex actr g10">
        <div className="fx actr g10">
          <FormattedMessage
            id={`invoice.pushed.to.integrationApp.${status?.toLowerCase()}`}
          />
        </div>
        {errorMessage && (
          <div className="mlauto">
            <Tooltip
              distance={10}
              html={<span className="ws-prewrap">{errorMessage}</span>}
              position="top-start"
              inertia
              trigger="mouseenter"
              animation="shift"
              animateFill={false}
              className="tooltip"
            >
              <SVGIcon name="InlineError" size="sm" color="red" />
            </Tooltip>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="position-relative">
      {!cell?.column?.hideError && (
        <div className="action-button-bg position-absolute full-width full-height flex center">
          {status !== null ? (
            renderStatus()
          ) : (
            <div className="full-width ellipsis text-black hidden">
              <DisplayText element="p" size="large">
                <FormattedMessage id="client.details.invoice.listView.action.empty.backGroundText" />
              </DisplayText>
            </div>
          )}
        </div>
      )}
      <div className="action-button flex center g20">
        <ButtonContainer loadingState={loadingState}>
          {status === 'FAILURE' && renderStatus()}
          <Button
            loading={actionLoading || loadingState || pushedToAppLoading}
            onClick={() => {
              actions.onClick(meta, integrationType, setActionLoading);
              setActionLoading(true);
            }}
          >
            <div className="flex actr">
              <ActiveIntegrationIcon
                integrationId={integrationType}
                height="19px"
                width="19px"
              />
              <span className="ml-5">
                <FormattedMessage
                  id={integrationMap[integrationType][pushActionKey]}
                />
              </span>
            </div>
          </Button>
        </ButtonContainer>
      </div>
    </div>
  );
}

const ButtonContainer = (props) => {
  const { intl, loadingState, children } = props;
  if (loadingState) {
    return (
      <Tooltip
        html={
          <p
            dangerouslySetInnerHTML={{
              __html: dompurifier(
                getTranslatedText(
                  intl,
                  'client.details.invoice.pushToThirdParty.loading'
                )
              )
            }}
          />
        }
        inertia
        trigger="mouseenter"
        animation="shift"
        animateFill={false}
        className="tooltip"
      >
        {children}
      </Tooltip>
    );
  }
  return <>{children}</>;
};

export default injectIntl(InvoiceIntegrationStatusCell);
